<template>
  <div>
    <div
      v-if="!isRegistrationRoute"
      class="md:hidden p-4 bg-primary-900 min-h-[70px]"
    >
      <img
        alt="Your Company"
        class="w-[80px] h-[44px] logo float-right"
        src="../../assets/logo-lg.svg"
      />

      <div
        class="float-left flex gap-3 text-white hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
      >
        <button @click="sidebarOpen = true">
          <span class="sr-only">Open sidebar</span>
          <span class="material-symbols-rounded text-3xl">menu</span>
        </button>
      </div>
    </div>

    <!-- The drawer is a modal surface, so it sits on the app's modal layer
         (50-70) rather than below it. At z-40 it was covered by the sale tabs
         strip (z-45) and by the order page's own fixed chrome (z-50): the
         bottom cart bar and the sticky search row.
         Keep this comment outside TransitionRoot: it renders as="template",
         which takes a single child, and in dev builds a comment counts. -->
    <TransitionRoot :show="sidebarOpen" as="template">
      <Dialog
        as="div"
        class="relative z-[60] md:hidden"
        @close="sidebarOpen = false"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-white bg-opacity-100"></div>
        </TransitionChild>

        <div class="fixed inset-0 z-40 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel
              class="relative flex w-full max-w-xs flex-1 flex-col bg-primary-900 pt-[45px] pb-4 mr-auto"
            >
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute top-0 left-0 pt-2">
                  <button
                    class="ml-1 flex h-10 w-10 items-center justify-center"
                    type="button"
                    @click="sidebarOpen = false"
                  >
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" class="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              <div class="mt-[30px] h-0 flex-1 overflow-y-auto">
                <nav class="space-y-1 px-8">
                  <template v-for="item in navigation" :key="item.name">
                    <h4
                      v-if="item.type === 'separator'"
                      class="pt-5 px-3 text-[#ACACAC]"
                    >
                      {{ item.name }}
                    </h4>
                    <div
                      v-else-if="item.href === '/schedule'"
                      class="bg-none text-primary-200 group flex flex-1 items-center px-2 py-2 text-base font-medium rounded-[4px] pointer-events-none"
                    >
                      <span
                        :class="[
                          'material-symbols-rounded text-base leading-[16px] ml-2',
                          item.href === '/schedule' ? 'disabled' : '',
                        ]"
                        >{{ item.icon }}</span
                      >
                      {{ item.name }}
                      <div
                        class="font-medium text-xs text-secondary-600 px-[6px] py-[2px] mr-2 rounded bg-secondary-200"
                      >
                        בקרוב
                      </div>
                    </div>
                    <router-link
                      v-else
                      :class="[
                        'group flex items-center px-2 py-3 text-base font-medium rounded-md bg-none text-primary-200 hover:text-primary-500 hover:bg-purple_semantic',
                        item.current ? 'bg-primary-200 text-primary-500' : '',
                      ]"
                      :to="item.href"
                      @click="sidebarOpen = false"
                    >
                      <span class="material-symbols-rounded text-[16px] ml-2">{{
                        item.icon
                      }}</span>
                      {{ item.name }}
                    </router-link>
                  </template>
                  <button
                    class="h-10 text-red-400 flex items-center px-2 py-10"
                    @click="logOutMobile"
                  >
                    <span
                      class="material-symbols-rounded text-base leading-[16px] ml-2"
                      >logout</span
                    >
                    יציאה
                  </button>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
          <div aria-hidden="true" class="w-0 flex-shrink-0"></div>
        </div>
      </Dialog>
    </TransitionRoot>
    <div
      v-if="!isRegistrationRoute"
      class="hidden md:fixed md:inset-y-0 md:flex md:w-[196px] md:flex-col z-[45] relative"
    >
      <onboarding-modal
        v-if="(step === 1 || step === 3) && showOnboarding"
      ></onboarding-modal>

      <div class="flex flex-grow flex-col bg-primary-900 overflow-hidden">
        <div
          class="flex items-center justify-center m-auto mt-[82px] mb-[52px] w-fit"
        >
          <img
            alt="Your Company"
            class="w-[106px] h-[44px] logo"
            src="../../assets/logo-lg.svg"
          />
        </div>
        <div
          ref="scrollContainer"
          :class="[
            'flex flex-1 flex-col justify-between sidebar',
            show ? '' : 'overflow-y-auto custom-side-scroll-bar',
          ]"
        >
          <nav class="flex-1 px-2">
            <template v-for="item in navigation" :key="item.name">
              <div
                v-if="item.type === 'separator'"
                class="mt-[28px] text-slate-400 flex items-center px-2 py-4 text-xs"
              >
                {{ item.name }}
              </div>
              <div
                v-else-if="item.href === '/schedule'"
                class="bg-none text-primary-200 group flex flex-1 items-center px-2 py-2 text-base font-medium rounded-[4px] pointer-events-none"
              >
                <span
                  :class="[
                    'material-symbols-rounded text-base leading-[16px] ml-2',
                    item.href === '/schedule' ? 'disabled' : '',
                  ]"
                  >{{ item.icon }}</span
                >
                {{ item.name }}
                <div
                  class="font-medium text-xs text-secondary-600 px-[6px] py-[2px] mr-2 rounded bg-secondary-200"
                >
                  בקרוב
                </div>
              </div>
              <router-link
                v-else
                :class="[
                  'group flex flex-1 items-center px-2 py-2 text-base font-medium rounded-[4px] bg-none text-primary-200 hover:text-primary-500 hover:bg-purple_semantic',
                  item.current ? 'bg-primary-200 text-primary-500' : '',
                  item.href === '/sales-board' && showOnboarding && step === 1
                    ? 'text-secondary-300'
                    : '',
                  item.href === '/contact' && showOnboarding && step === 3
                    ? 'text-secondary-300'
                    : '',
                  item.href === '/faq' && showOnboarding && step === 3
                    ? 'text-secondary-300'
                    : '',
                ]"
                :to="item.href"
              >
                <span
                  class="material-symbols-rounded text-base leading-[16px] ml-2"
                  >{{ item.icon }}</span
                >
                {{ item.name }}
              </router-link>
            </template>
          </nav>
        </div>
        <div class="flex flex-col px-2 mb-[30px]">
          <button
            class="h-10 text-red-400 flex items-center px-2 py-2 right-2"
            @click="handleLogOut"
          >
            <span class="material-symbols-rounded text-base leading-[16px] ml-2"
              >logout</span
            >
            יציאה
          </button>
        </div>
      </div>
    </div>

    <div :class="isRegistrationRoute ? '' : 'md:pr-[196px]'">
      <main
        :class="[
          $route.path === '/order' ? 'pl-0' : '',
          isRegistrationRoute
            ? 'p-0 pb-0 md:p-0 md:pb-0 xl:pb-0'
            : 'p-[20px] pb-[100px] md:p-10 md:pb-[120px] xl:pb-10',
        ]"
      >
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import OnboardingModal from "@/components/Onboarding/Onboarding.vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import axiosInstance from "@/helpers/axios";
import ButtonSecondary from "@/components/Global/ButtonSecondary.vue";

export default {
  components: {
    ButtonSecondary,
    DialogPanel,
    OnboardingModal,
    TransitionRoot,
    TransitionChild,
    Dialog,
    XMarkIcon,
  },
  props: {
    isRegistrationRoute: {
      required: true,
    },
  },
  data() {
    return {
      currentNavPath: "",
      navigationBase: [
        {
          name: "לוח מכירות",
          href: "/sales-board",
          icon: "home",
          current: false,
        },
        {
          name: "לו״ז מכירות",
          href: "/schedule",
          icon: "calendar_today",
          current: false,
        },
        { name: "הגדרות", type: "separator", current: false },
        {
          name: "פרופיל משפחתי",
          href: "/profile",
          icon: "account_circle",
          current: false,
        },
        {
          name: "אמצעי תשלום",
          href: "/payment-methods",
          icon: "credit_card",
          current: false,
        },
        {
          name: "זיכויים וחיובים",
          href: "/charges",
          icon: "sell",
          current: false,
        },
        {
          name: "עדכון משקל",
          href: "/product-scale",
          icon: "scale",
          current: false,
        },
        {
          name: "ההזמנות שלנו",
          href: "/my-orders",
          icon: "list_alt",
          current: false,
        },
        { name: "תמיכה", type: "separator" },
        { name: "שאלות נפוצות", href: "/faq", icon: "quiz", current: false },
        { name: "תקנון", href: "/policy", icon: "gavel", current: false },
        {
          name: "מדיניות החזרות",
          href: "/return-policy",
          icon: "assignment_return",
          current: false,
        },
      ],
      sidebarOpen: false,
    };
  },
  computed: {
    navigation() {
      const supportItem = {
        name: "פניות לתמיכה",
        href: "/support/tickets",
        icon: "call",
        current: false,
      };
      const faqIndex = this.navigationBase.findIndex((i) => i.href === "/faq");
      const result = [...this.navigationBase, supportItem];
      result.splice(faqIndex + 1, 0, result.pop());
      return result.map((item) => ({
        ...item,
        current: item.href === this.currentNavPath,
      }));
    },
    ...mapState({
      show: (state) => state.onboarding.show,
      step: (state) => state.onboarding.step,
      credits: (state) => state.user.creditsNumber,
      weights: (state) => state.user.weightsNumber,
      showOnboarding: (state) => state.onboarding.show,
      previousPage: (state) => state.orderDetails.previousPage,
    }),
  },
  watch: {
    $route() {
      this.updateCurrentNavigation();
    },
  },
  methods: {
    redirectToOldSite() {
      window.location.replace("https://mishnatyosef.org/login");
    },
    handleLogOut() {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Reset relevant user state so next login re-evaluates refund popup logic correctly
      if (this.setRefundToCard && this.setRefundToCardNoSetCount) {
        this.setRefundToCard(false);
        this.setRefundToCardNoSetCount(-1);
      }
      if (this.setOrdersCount) this.setOrdersCount(0);
      if (this.setTime) this.setTime("");
      this.$router.push("/login");
    },
    logOutMobile() {
      this.handleLogOut();
      setTimeout(() => {
        this.sidebarOpen = false;
      }, 100);
    },
    updateCurrentNavigation() {
      let currentPath = this.$route.path;

      if (
        this.$route.path === "/orders-history" ||
        this.$route.path.includes("/order/")
      ) {
        currentPath = "/my-orders";
      }
      if (this.$route.path.includes("/order/")) {
        if (
          this.previousPage === "/my-orders" ||
          this.previousPage === "/orders-history"
        ) {
          currentPath = "/my-orders";
        } else {
          currentPath = "/sales-board";
        }
      }
      if (this.$route.path === "/change-station") {
        currentPath = "/profile";
      }
      if (this.$route.path.startsWith("/support")) {
        currentPath = "/support/tickets";
      }
      if (currentPath && !this.showOnboarding)
        this.currentNavPath = currentPath;
    },
    ...mapMutations({
      setWeightsNumber: "user/setWeightsNumber",
      setCreditsNumber: "user/setCreditsNumber",
      setFullName: "user/setFullName",
      setNoLimit: "user/setNoLimit",
      setRefundToCard: "user/setRefundToCard",
      setRefundToCardNoSetCount: "user/setRefundToCardNoSetCount",
      setOrdersCount: "user/setOrdersCount",
      setTime: "user/setTime",
      setTotalLoginCartCount: "user/setTotalLoginCartCount",
      setDebts: "debtsModule/setDebts",
    }),
  },
  async mounted() {
    this.updateCurrentNavigation();

    if (!this.credits || !this.weights) {
      let current = {};
      const { data } = await axiosInstance.get("/profile");
      this.setCreditsNumber(data.open_transactions_count);
      this.setWeightsNumber(data.open_weights.length);
      this.setFullName(data.full_name);
      this.setNoLimit(data.no_limit);
      this.setDebts(data.debts || []);

      this.setRefundToCard(data.refund_to_card);
      this.setRefundToCardNoSetCount(data.refund_to_card_no_set_count);
      this.setOrdersCount(data.orders_count);
      this.setTime(data.site.time);
      try {
        current = JSON.parse(localStorage.getItem("user") || "{}");
      } catch {}
      const merged = {
        ...current,
        ...data,
        meta_data: data.meta_data ?? current.meta_data ?? {},
      };
      localStorage.setItem("user", JSON.stringify(merged));
      this.setTotalLoginCartCount(Number(merged?.meta_data?.login_count ?? 0));
    }
  },
};
</script>

<style>
/* Your existing styles */
.logo {
  width: 106px;
  height: 44px;
}
</style>
